import React, {useState, useEffect} from 'react';
import Cookies from 'js-cookie';
import { Radar } from 'react-chartjs-2';
import logoIcon from "../../assets/logo-icon.png";

const translations = {
  en: {
    riasecCareerInterests: "RIASEC Career Interests",
    riasecModel: "RIASEC Model",
    showJobAlignment: "Show Job Alignment",
    showCareerDevelopmentSuggestions: "Show Career Development Suggestions",
    alignment: "Alignment",
    careerPath: "Career Path",
    typeSummary: "Type Summary",
    recruiterSummary: (primary, secondary, realAlign, socialAlign) => `This candidate shows a ${primary}-${secondary} profile, demonstrating strong capabilities in ${realAlign}. Their secondary strength in ${secondary} suggests additional competency in ${socialAlign}. This combination indicates high potential for roles requiring both ${primary.toLowerCase()} and ${secondary.toLowerCase()} characteristics.`,
    candidateSummary: (primary, secondary, realAlign, socialAlign) => `Your RIASEC assessment reveals a primary ${primary} orientation with ${secondary} as your secondary type. This combination suggests you thrive in environments that combine ${realAlign} with elements of ${socialAlign}. This profile can guide you toward career paths that align with your natural interests and strengths.`
  },
  nl: {
    riasecCareerInterests: "RIASEC Carrière-interesses",
    riasecModel: "RIASEC Model",
    showJobAlignment: "Toon Taakafstemming",
    showCareerDevelopmentSuggestions: "Toon Carrière-ontwikkelingssuggesties",
    alignment: "Afstemming",
    careerPath: "Carrièrepad",
    typeSummary: "Type Samenvatting",
    recruiterSummary: (primary, secondary, realAlign, socialAlign) => `Deze kandidaat toont een ${primary}-${secondary} profiel, met sterke capaciteiten in ${realAlign}. De secundaire kracht in ${secondary} duidt op extra competentie in ${socialAlign}. Deze combinatie wijst op een hoog potentieel voor functies die zowel ${primary.toLowerCase()} als ${secondary.toLowerCase()} kenmerken vereisen.`,
    candidateSummary: (primary, secondary, realAlign, socialAlign) => `Je RIASEC-beoordeling laat een primaire ${primary} oriëntatie zien met ${secondary} als je secundaire type. Deze combinatie suggereert dat je floreert in omgevingen die ${realAlign} combineren met elementen van ${socialAlign}. Dit profiel kan je helpen bij het kiezen van carrièrepaden die aansluiten bij je natuurlijke interesses en sterke punten.`
  }
};

const RIASECModel = ({ riasecData, analysisResults, colors, isRecruiterMode, isGeneratingPDF, isDutch = false }) => {
    const [lang, setLang] = useState(isDutch ? 'nl' : 'en');
    const t = translations[lang];
    const [isRiasecToggleOpen, setIsRiasecToggleOpen] = useState(false)

    const riasecOptions = {
        scales: {
            r: {
                angleLines: {
                    display: true
                },
                suggestedMin: 0,
                suggestedMax: 6,
                ticks: {
                    stepSize: 1
                }
            }
        },
        plugins: {
            legend: {
                display: false
            }
        }
    };

    const toggleOpen = () => {
        setIsRiasecToggleOpen(!isRiasecToggleOpen);
        Cookies.set('isRiasecToggleOpen', !isRiasecToggleOpen)
      };

    useEffect(() => {
        const checkLanguage = () => {
            const cookieIsDutch = Cookies.get('isDutch') === 'true';
            const newLang = cookieIsDutch ? 'nl' : 'en';
            if (newLang !== lang) {
                setLang(newLang);
            }
        };
        checkLanguage();
        const interval = setInterval(checkLanguage, 1000);
        return () => clearInterval(interval);
    }, [lang]);

    useEffect(() => {
        setIsRiasecToggleOpen(true)
    }, [isRecruiterMode])

    return (
        <div className="flex flex-col animate-text">
            <div className='flex items-center justify-between gap-2'>
            <h2 className="text-xl font-bold text-primary">
                {isRecruiterMode ? t.riasecCareerInterests : t.riasecModel}
            </h2>
            {!isGeneratingPDF && (
            <div className="toggle-container !m-0 !gap-2">
            <label className='text-sm'>
            {isRecruiterMode ? t.showJobAlignment : t.showCareerDevelopmentSuggestions}
            </label>
            <input
                type="checkbox"
                checked={isRiasecToggleOpen}
                onChange={toggleOpen}
                className="theme-checkbox"
                />
            </div>
             )}
            </div>
            <div className="flex-1 border p-4 rounded-md w-full h-fit mt-2 flex flex-col gap-4">
                <div className="w-full h-[300px] flex items-center justify-center animate-text">
                    <Radar data={riasecData} options={riasecOptions} />
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-4">
                    {Object.entries(analysisResults?.riasec)?.map(([type, data], index) => (
                        type !== 'primaryType' && type !== 'secondaryType' && type !== 'suggestedCareers' && (
                            <div className="animate-text flex flex-col" key={index}>
                                <div className='flex-1'>
                                    <div className="flex justify-between gap-2 items-center mt-2">
                                        <h3 className="font-bold capitalize">{type}</h3>
                                        <p className="text-gray-500 text-sm">{data.score}/5</p>
                                    </div>
                                    <div className="relative w-full h-2 bg-gray-200 rounded mt-2">
                                        <div
                                            className="absolute h-full bg-default rounded"
                                            style={{ width: `${(data.score)*20}%`, backgroundColor: colors[index] }}
                                        />
                                    </div>
                                    <p className="text-sm text-gray-600 mt-4">{data.description}</p>
                                    </div>
                                    {isRiasecToggleOpen && isRecruiterMode ? (
                                        isRiasecToggleOpen &&  data.jobAlignment && (
                                            <div className="text-sm mt-4 font-medium p-2 rounded-md gradient-bg-1 ">
                                            <p><span className="font-semibold">{t.alignment}:</span> {data.jobAlignment}</p>
                                        </div>
                                        )
                                    ) : (
                                        isRiasecToggleOpen &&  data.careerDevelopment && (
                                            <div className="text-sm mt-4 font-medium p-2 rounded-md gradient-bg-1">
                                                <p><span className="font-semibold">{t.careerPath}:</span> {data.careerDevelopment}</p>
                                            </div>
                                        )
                                    )}
                            </div>
                        )
                    ))}
                </div>
                <div className="border-t pt-4 mb-1">
                        <div className='p-4 rounded-md gradient-bg-2'>
                        <span className="text-primary text-sm font-bold mb-1 mt-neg flex items-center gap-2 bg-white py-1.5 px-3 pr-4 rounded-full w-fit mb-4">
                        <img src={logoIcon} className="size-4" />
                        <h3>{t.typeSummary}</h3>
                        </span>
                        <p className="text-sm mt-2">
                        {isRecruiterMode 
                            ? t.recruiterSummary(
                                analysisResults.riasec.primaryType,
                                analysisResults.riasec.secondaryType,
                                analysisResults.riasec.realistic.jobAlignment,
                                analysisResults.riasec.social.jobAlignment
                              )
                            : t.candidateSummary(
                                analysisResults.riasec.primaryType,
                                analysisResults.riasec.secondaryType,
                                analysisResults.riasec.realistic.jobAlignment,
                                analysisResults.riasec.social.jobAlignment
                              )
                        }
                        </p>
                        </div>
                    </div>
            </div>
        </div>
    );
};

export default RIASECModel;